// What to play. Pure functions: the game state in, the cue out. The engine does the audio.
//
//   screen          war    bed
//   loading         -      theme
//   leader-select   -      theme
//   game            no     explore
//   game            yes    tension
//   sandbox         no     sandbox, then explore, then sandbox... one pass each
//   sandbox         yes    tension
//
// A sandbox game lasts 20 to 40 minutes against a bed of 2 to 3, so at peace the two beds take turns:
//
//   sandbox ████████████████╲╱████████████████╲  explore ╱████████...
//                    BED_LEAD_S ^ the switch starts this long before the file ends

import type { BedCue, MusicEvent, MusicState, Screen, StingCue } from './types.ts';

/** A war flip (peace bed <-> tension) waits until the current bed has played this long. */
export const BED_MIN_DWELL_S = 20;

/**
 * At peace in the sandbox the next bed starts this long before the playing one ends. It covers the
 * 3 s bed crossfade plus the 3 s the file's own loop blend needs, so that blend never starts.
 */
export const BED_LEAD_S = 7;

// Beds that play while a game runs, a war flip being a change to or from tension among them.
const PLAY_BEDS: readonly BedCue[] = ['explore', 'tension', 'sandbox'];

// What the sandbox plays at peace, in turn.
const SANDBOX_PEACE: readonly BedCue[] = ['sandbox', 'explore'];

const STINGS: Record<MusicEvent, StingCue> = {
  'war-declared': 'war',
  'deal-signed': 'deal',
  'turn-advanced': 'turn',
  'victory-opened': 'victory',
};

// The more important sting wins when two arrive together: a turn chime never cuts a war horn.
const PRIORITY: Record<StingCue, number> = { turn: 0, deal: 1, war: 2, victory: 3 };

export function bedFor(state: MusicState): BedCue {
  if (state.screen === 'sandbox') {
    return state.atWar ? 'tension' : 'sandbox';
  }
  if (state.screen !== 'game') {
    return 'theme';
  }
  return state.atWar ? 'tension' : 'explore';
}

export function stingFor(event: MusicEvent): StingCue {
  return STINGS[event];
}

export interface BedPlan {
  cue: BedCue;
  /** Clock time (seconds) the switch may happen; at or before `now` means at once. */
  at: number;
}

export interface BedPlaying {
  cue: BedCue;
  /** Clock time the bed started. */
  since: number;
  /** Length of its file. */
  seconds: number;
  /** The screen the bed was chosen for. */
  screen: Screen;
}

/**
 * The bed to play and when to switch to it. Flipping between peace and war music right after the
 * last flip would ping-pong when the war flag flickers, so that one switch waits out the dwell.
 * At peace in the sandbox the playing bed gives way to the other one when its pass is nearly over.
 */
export function planBed(state: MusicState, playing: BedPlaying | null, now: number): BedPlan {
  const cue = bedFor(state);
  if (playing === null) {
    return { cue, at: now };
  }

  const atPeace = state.screen === 'sandbox' && !state.atWar && playing.screen === 'sandbox';
  if (atPeace && SANDBOX_PEACE.includes(playing.cue)) {
    const next = SANDBOX_PEACE.find((other) => other !== playing.cue) ?? cue;
    const pass = Math.max(BED_MIN_DWELL_S, playing.seconds - BED_LEAD_S);
    return { cue: next, at: Math.max(now, playing.since + pass) };
  }

  const flipsWar = (playing.cue === 'tension') !== (cue === 'tension');
  const warFlip = flipsWar && PLAY_BEDS.includes(playing.cue) && PLAY_BEDS.includes(cue);
  if (!warFlip) {
    return { cue, at: now };
  }
  return { cue, at: Math.max(now, playing.since + BED_MIN_DWELL_S) };
}

export interface StingPlaying {
  cue: StingCue;
  /** Clock time the sting ends. */
  endsAt: number;
}

export type StingVerdict = 'play' | 'drop';

export function judgeSting(next: StingCue, playing: StingPlaying | null, now: number): StingVerdict {
  if (playing === null || now >= playing.endsAt) {
    return 'play';
  }
  return PRIORITY[next] > PRIORITY[playing.cue] ? 'play' : 'drop';
}
